import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, KeyRound, Check, PlugZap, Unplug, RefreshCcw, ShieldAlert, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

// Static provider metadata — kept client-side so we don't need a
// separate metadata endpoint for the settings screen.
const PROVIDERS = [
  {
    id: "axonaut",
    name: "Axonaut",
    tagline: "Facturation & CRM pour TPE/PME",
    keyDocUrl: "https://axonaut.com/api",
    keyDocLabel: "Générer une clé personnelle → Réglages > API dans Axonaut",
  },
  {
    id: "pennylane",
    name: "Pennylane",
    tagline: "Comptabilité collaborative",
    keyDocUrl: "https://pennylane.readme.io/",
    keyDocLabel: "Générer un token personnel → Réglages > Développeurs dans Pennylane",
  },
];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); }
  catch { return iso; }
};

export default function Connectors() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("accounting.write");

  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);          // provider id or null
  const [connectDialog, setConnectDialog] = useState(null); // provider or null
  const [apiKey, setApiKey] = useState("");
  const [disconnectDialog, setDisconnectDialog] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/connectors");
      setState(r.data.connectors || {});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitApiKey = async (provider) => {
    if (!apiKey.trim()) return;
    setBusy(provider);
    try {
      await api.post(`/connectors/${provider}/api-key`, { api_key: apiKey.trim() });
      toast.success(`${provider} : connexion validée`);
      setConnectDialog(null);
      setApiKey("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (provider) => {
    setBusy(provider);
    try {
      await api.delete(`/connectors/${provider}`);
      toast.success(`${provider} déconnecté`);
      setDisconnectDialog(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const runSync = async (provider) => {
    setBusy(provider);
    try {
      const r = await api.post(`/connectors/${provider}/sync`, {});
      const s = r.data?.stats || {};
      toast.success(
        `Sync ${provider} : ${(s.supplier_created || 0) + (s.customer_created || 0)} créés, ` +
        `${(s.supplier_updated || 0) + (s.customer_updated || 0)} mis à jour`
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="connectors-page">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900">Connecteurs comptables</h1>
        <p className="text-sm text-slate-600 mt-2">
          Reliez Averdy Agency à votre outil comptable actuel. Deux modes disponibles :
          OAuth2 (partenariat officiel, en cours de mise en place) ou{" "}
          <strong>clé API personnelle</strong> — utilisable dès aujourd&apos;hui, générée
          depuis votre compte Axonaut ou Pennylane.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {PROVIDERS.map((p) => {
          const s = state[p.id] || {};
          const connected = !!s.connected;
          return (
            <Card key={p.id}
                  data-testid={`connector-card-${p.id}`}
                  className="p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{p.name}</h2>
                  <p className="text-sm text-slate-500">{p.tagline}</p>
                </div>
                {connected ? (
                  <Badge variant="default" data-testid={`badge-${p.id}-connected`}>
                    <Check className="w-3 h-3 mr-1" /> Connecté
                  </Badge>
                ) : (
                  <Badge variant="outline" data-testid={`badge-${p.id}-disconnected`}>
                    Non connecté
                  </Badge>
                )}
              </div>

              <div className="text-sm text-slate-600 space-y-1">
                <div>
                  <span className="text-slate-500">Mode :</span>{" "}
                  {s.mode === "api_key" ? "Clé API personnelle"
                    : s.mode === "oauth" ? "OAuth2 (partenariat)"
                    : "—"}
                </div>
                <div>
                  <span className="text-slate-500">OAuth2 disponible :</span>{" "}
                  {s.oauth_available
                    ? <Badge variant="secondary">Actif</Badge>
                    : <span className="text-slate-500 italic">
                        En attente de partenariat
                      </span>}
                </div>
                <div>
                  <span className="text-slate-500">Synchro auto :</span>{" "}
                  <span data-testid={`schedule-${p.id}`}>
                    toutes les {Math.round((s.schedule?.interval_seconds || 0) / 60 * 10) / 10} min
                  </span>
                  {s.schedule_overridden
                    ? <Badge variant="outline" className="ml-2 text-[10px]">override</Badge>
                    : <span className="ml-1 text-slate-400 text-xs" title={s.schedule?.rate_limit_note}>
                        (défaut {p.name})
                      </span>}
                </div>
                {connected && (
                  <>
                    <div>
                      <span className="text-slate-500">Dernière vérification :</span>{" "}
                      {fmtDateTime(s.last_check_at)}{" "}
                      {s.last_check_ok === true && <Check className="inline w-3 h-3 text-emerald-600" />}
                    </div>
                    <div>
                      <span className="text-slate-500">Dernière synchro :</span>{" "}
                      {fmtDateTime(s.last_sync_at)}
                      {s.last_sync_source === "scheduler" && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">auto</Badge>
                      )}
                    </div>
                    {s.last_sync_error && (
                      <div className="text-amber-700 text-xs">
                        ⚠︎ Dernière erreur : {String(s.last_sync_error).slice(0, 120)}
                      </div>
                    )}
                    {Array.isArray(s.last_sync_stats?.forbidden_endpoints)
                      && s.last_sync_stats.forbidden_endpoints.length > 0 && (
                      <div className="text-amber-700 text-xs"
                           data-testid={`forbidden-endpoints-${p.id}`}>
                        ⚠︎ Modules non autorisés par votre clé :{" "}
                        {s.last_sync_stats.forbidden_endpoints.join(", ")}
                        {" — "}
                        <a href={p.keyDocUrl} target="_blank" rel="noreferrer"
                           className="underline">
                          régénérez une clé avec plus de scopes
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {!connected && canWrite && (
                  <Button
                    onClick={() => setConnectDialog(p)}
                    data-testid={`connect-${p.id}-btn`}
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> Connecter avec une clé API
                  </Button>
                )}
                {connected && canWrite && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => runSync(p.id)}
                      disabled={busy === p.id}
                      data-testid={`sync-${p.id}-btn`}
                    >
                      {busy === p.id
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <RefreshCcw className="w-4 h-4 mr-1" />}
                      Synchroniser
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setDisconnectDialog(p)}
                      data-testid={`disconnect-${p.id}-btn`}
                    >
                      <Unplug className="w-4 h-4 mr-1" /> Déconnecter
                    </Button>
                  </>
                )}
                <a href={p.keyDocUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center text-sm text-blue-600 hover:underline ml-auto">
                  Doc API <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-slate-700">
            <div className="font-medium">Vos clés sont chiffrées</div>
            Votre clé API est stockée <strong>chiffrée AES-256-GCM par tenant</strong> —
            le même mécanisme que vos IBAN et coordonnées bancaires. Aucun opérateur Averdy Agency
            ne peut la lire en clair. En cas de déconnexion, la clé est effacée
            immédiatement et les enregistrements importés restent horodatés pour audit.
          </div>
        </div>
      </Card>

      {/* --- Connect dialog: paste API key --- */}
      <Dialog open={!!connectDialog}
              onOpenChange={(v) => { if (!v) { setConnectDialog(null); setApiKey(""); } }}>
        <DialogContent data-testid="connect-dialog">
          <DialogHeader>
            <DialogTitle>Connecter {connectDialog?.name}</DialogTitle>
            <DialogDescription>{connectDialog?.keyDocLabel}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="api_key">Clé API personnelle</Label>
            <Input
              id="api_key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Collez votre clé ici"
              data-testid="api-key-input"
            />
            <p className="text-xs text-slate-500">
              La clé sera validée en direct auprès de {connectDialog?.name} avant d&apos;être
              enregistrée. Une clé incorrecte ne sera pas conservée.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost"
                    onClick={() => { setConnectDialog(null); setApiKey(""); }}>
              Annuler
            </Button>
            <Button
              onClick={() => submitApiKey(connectDialog.id)}
              disabled={!apiKey.trim() || busy === connectDialog?.id}
              data-testid="submit-api-key-btn"
            >
              {busy === connectDialog?.id
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <PlugZap className="w-4 h-4 mr-1" />}
              Valider et connecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Disconnect confirmation --- */}
      <AlertDialog open={!!disconnectDialog}
                    onOpenChange={(v) => { if (!v) setDisconnectDialog(null); }}>
        <AlertDialogContent data-testid="disconnect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Déconnecter {disconnectDialog?.name} ?</AlertDialogTitle>
            <AlertDialogDescription>
              La clé API sera effacée immédiatement. Les factures déjà importées
              via ce connecteur seront <strong>conservées</strong> dans votre dossier
              Averdy Agency mais taguées «&nbsp;connecteur déconnecté le … » pour audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => disconnect(disconnectDialog.id)}
              data-testid="confirm-disconnect-btn"
            >
              Déconnecter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
